import { createAction, createReducer, nanoid } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setRequested, setReceived, setFailed } from "modules/loader-watchers/helpers/loading"
import { BOOKING_MODES } from "pagesV2/pro/Bookings/constants"
import { CART_ERRORS, CART_HOLD_STATUSES } from "pagesV2/BookingWidget/constants"
import { buildCartHoldPayload } from "pagesV2/BookingWidget/WidgetCheckout/payment"

export const requestedExperiences = createAction("experiences/requested")
export const receivedExperiences = createAction("experiences/received")
export const failedExperiences = createAction("experiences/failed")
export const cleanedExperiences = createAction("experiences/cleaned")
export const requestedExperiencesByGuide = createAction("experiences/byGuide/requested")
export const receivedExperiencesByGuide = createAction("experiences/byGuide/received")
export const failedExperiencesByGuide = createAction("experiences/byGuide/failed")
export const cleanExperiencesByGuide = createAction("experiences/byGuide/clean")
export const requestedExperienceById = createAction("experiences/byId/requested")
export const receivedExperienceById = createAction("experiences/byId/received")
export const failedExperienceById = createAction("experiences/byId/failed")
export const requestedExperienceBookingData = createAction("experiences/bookingData/requested")
export const receivedExperienceBookingData = createAction("experiences/bookingData/received")
export const failedExperienceBookingData = createAction("experiences/bookingData/failed")
export const requestedEmbeddedCreateBooking = createAction("experiences/createBooking/requested")
export const receivedEmbeddedCreateBooking = createAction("experiences/createBooking/received")
export const failedEmbeddedCreateBooking = createAction("experiences/createBooking/failed")
export const requestedEmbeddedUpdateBooking = createAction("experiences/updateBooking/requested")
export const receivedEmbeddedUpdateBooking = createAction("experiences/updateBooking/received")
export const failedEmbeddedUpdateBooking = createAction("experiences/updateBooking/failed")
export const requestedEmbeddedCreatePaymentIntent = createAction("experiences/createPaymentIntent/requested")
export const receivedEmbeddedCreatePaymentIntent = createAction("experiences/createPaymentIntent/received")
export const failedEmbeddedCreatePaymentIntent = createAction("experiences/createPaymentIntent/failed")
export const requestedEmbeddedGetBookingDetailsById = createAction("experiences/getBookingDetailsById/requested")
export const receivedEmbeddedGetBookingDetailsById = createAction("experiences/getBookingDetailsById/received")
export const failedEmbeddedGetBookingDetailsById = createAction("experiences/getBookingDetailsById/failed")
export const updateBookingSelections = createAction("experiences/bookingSelections/update")
export const resetBookingSelections = createAction("experiences/bookingSelections/reset")
// Each cart item gets its own id when added, so the same experience can be in the cart twice.
export const addWidgetCartItem = createAction("experiences/widgetCart/add", (item) => ({
  payload: { ...item, cartId: item.cartId || nanoid() }
}))
export const updateWidgetCartItem = createAction("experiences/widgetCart/update", (cartId, changes) => ({ payload: { cartId, changes } }))
export const removeWidgetCartItem = createAction("experiences/widgetCart/remove")
export const restoreWidgetCart = createAction("experiences/widgetCart/restore")
// The API's cart (every cart call answers with it). `receivedAt` turns its expiry into this
// device's clock: expires_at − server_time from now, whatever the device's time is.
export const receivedWidgetCart = createAction("experiences/widgetCart/received", (cart) => ({ payload: cart, meta: { receivedAt: Date.now() } }))
// The API no longer knows the cart (old or unknown token): nothing is held any more.
export const forgetWidgetCartHolds = createAction("experiences/widgetCart/holds/forget")
// A cart item whose places couldn't be held again, with the API's error.
export const loseWidgetCartItemHold = createAction("experiences/widgetCart/holds/lose", (cartId, error) => ({ payload: { cartId, error } }))
export const clearWidgetCart = createAction("experiences/widgetCart/clear")
// Checkout is taking a payment: the cart's timer and its "still shopping?" popup are hidden meanwhile.
export const setWidgetCartPaying = createAction("experiences/widgetCart/paying")
export const resetExperienceBookingData = createAction("experiences/bookingData/reset")
export const resetExperienceById = createAction("experiences/experienceById/reset")
export const resetBookingInfo = createAction("experiences/bookingInfo/reset")
export const cleanExperiencesList = createAction("experiences/list/clean")

export const requestedTripAvailableSlots = createAction("experiences/tripAvailableSlots/requested")
export const receivedTripAvailableSlots = createAction("experiences/tripAvailableSlots/received")
export const failedTripAvailableSlots = createAction("experiences/tripAvailableSlots/failed")
export const resetTripAvailableSlots = createAction("experiences/tripAvailableSlots/reset")

export const requestedEmail = createAction("experiences/email/requested")
export const receivedEmail = createAction("experiences/email/received")
export const failedEmail = createAction("experiences/email/failed")

const initialState = {
  ...LOADING_INITIAL_STATE,
  list: [],
  byGuide: {
    loading: false,
    error: null,
    data: [],
    metaData: {},
    guide: {
      adventureMode: "sportsman"
    }
  },
  experienceById: {
    loading: false,
    error: null,
    experienceDetail: {},
    widgetLogoUrl: null
  },
  bookingInfo: {
    loading: false,
    error: null,
    data: {},
    guestToken: null
  },
  experienceBookingData: {
    loading: false,
    error: null,
    bookingData: {},
    // Which trip_price_id (multi-day duration option) the in-flight request and the
    // currently-held bookingData were each fetched for — lets a consumer tell a response
    // that actually matches what's selected right now apart from one for a duration the
    // guide has since switched away from, or one that arrived out of order behind a
    // later request's own response. See receivedExperienceBookingData and
    // ExperienceDetail.jsx's isMultiDayAvailabilityPending, which rely on this.
    requestedTripPriceId: null,
    bookingDataTripPriceId: null,
    bookingSelections: {
      date: null,
      // Dynamic multi-day only: the guest-chosen end of the date range (start lives in `date`).
      // Client-side state for the range picker and the price preview — a booking is stored as a
      // start date plus a duration, never as an end date, so this is not part of any payload.
      endDate: null,
      counts: {
        main: {
          adults: 0,
          children: 0,
          infants: 0,
          passengers: 0
        },
        extra: {
          adults: 0,
          children: 0,
          infants: 0,
          passengers: 0
        }
      },
      tripLength: null,
      timeSlot: null,
      amount: 0,
      baseAmount: 0,
      breakdown: null
    }
  },
  confirmedBookingData: {
    loading: false,
    error: null,
    data: {}
  },
  tripAvailableSlots: {
    loading: false,
    error: null,
    data: null
  },
  // The new booking widget's cart: the booking selections the guest added, one guide's experiences
  // (private and ticketed). Kept apart from bookingSelections (the experience page's draft) so
  // browsing another experience leaves it alone.
  widgetCart: {
    // Each item also carries its API hold: holdId, holdStatus (CART_HOLD_STATUSES), holdError.
    items: [],
    // { expiresAt (this device's clock), extensionsLeft }: the API's one timer for the whole cart.
    hold: null,
    // The API cart's token, sent as Cart-Token; null until the first hold.
    cartToken: null,
    // A payment is running (setWidgetCartPaying). Never saved with the cart.
    isPaying: false
  }
}

const NO_ITEM_HOLD = { holdId: null, holdStatus: null, holdError: null }

// An item's fields from the API cart's hold for it (matched by client_item_id = cartId).
const mapItemHold = (hold) => (hold ? { holdId: hold.hold_id, holdStatus: hold.status, holdError: hold.error || null } : NO_ITEM_HOLD)

const mapCartHold = (cart, receivedAt) => {
  if (!cart?.expires_at || !cart?.server_time) return null
  return {
    expiresAt: receivedAt + (Date.parse(cart.expires_at) - Date.parse(cart.server_time)),
    extensionsLeft: Number(cart.extensions_left) || 0
  }
}

// A start time is the ticketed scheduled slot, or the private trip's time slot label.
const getCartSlotStart = (item) => item?.scheduledSlot?.id ?? item?.timeSlot ?? null
const isSameCartSlot = (a, b) => a.experienceId === b.experienceId && a.date === b.date && getCartSlotStart(a) === getCartSlotStart(b)

export const widgetCartItemsSelector = (state) => state.experiences.widgetCart.items
export const widgetCartHoldSelector = (state) => state.experiences.widgetCart.hold
export const widgetCartTokenSelector = (state) => state.experiences.widgetCart.cartToken
export const widgetCartPayingSelector = (state) => state.experiences.widgetCart.isPaying

const experiencesReducer = createReducer(initialState, {
  [requestedExperiences]: (state) => setRequested(state),
  [receivedExperiences]: (state, action) => {
    setReceived(state)
    state.list = action.payload
  },
  [failedExperiences]: (state) => setFailed(state),
  [cleanedExperiences]: () => initialState,
  [requestedExperiencesByGuide]: (state) => {
    state.byGuide.loading = true
    state.byGuide.error = null
  },
  [receivedExperiencesByGuide]: (state, action) => {
    state.byGuide.loading = false
    const newData = action.payload.data
    state.byGuide.data = [...state.byGuide.data, ...newData]
    state.byGuide.metaData = action.payload.metadata
    state.byGuide.adventureMode = action.payload.guide.adventure_mode
    state.byGuide.widgetLogoUrl = action.payload?.guide?.logo_variants?.widget_large || action.payload?.guide?.logo || null
    state.byGuide.businessName = action.payload?.guide?.business_name || action.payload?.guide?.company_name || null
    state.byGuide.slug = action.payload?.guide?.slug || null
    state.byGuide.rating = Number(action.payload?.guide?.rating) || null
  },
  [failedExperiencesByGuide]: (state, action) => {
    state.byGuide.loading = false
    state.byGuide.error = action.payload
  },
  [cleanExperiencesByGuide]: (state) => {
    state.byGuide.error = {}
    state.byGuide.data = []
    state.byGuide.metaData = {}
    state.byGuide.adventureMode = ""
    state.byGuide.widgetLogoUrl = null
    state.byGuide.businessName = null
    state.byGuide.slug = null
    state.byGuide.rating = null
  },
  [requestedExperienceById]: (state) => {
    state.experienceById.loading = true
    state.experienceById.error = null
  },
  [receivedExperienceById]: (state, action) => {
    state.experienceById.loading = false
    state.experienceById.experienceDetail = action.payload.data
    state.experienceById.widgetLogoUrl = action.payload.data?.guide?.logo_variants?.widget_large || null
    state.experienceById.businessName = action.payload.data?.guide?.company_name || null
  },
  [failedExperienceById]: (state, action) => {
    state.experienceById.loading = false
    state.experienceById.error = action.payload
  },
  [requestedExperienceBookingData]: (state, action) => {
    state.experienceBookingData.loading = true
    state.experienceBookingData.error = null
    state.experienceBookingData.requestedTripPriceId = action.props?.tripPriceId ?? null
  },
  [receivedExperienceBookingData]: (state, action) => {
    const responseTripPriceId = action.props?.tripPriceId ?? null
    // A response for a duration other than the one most recently requested is either
    // stale (the guide switched duration before this one came back) or out of order
    // (a newer request's own response already landed first) — either way it must not
    // overwrite the current data. Leaving `loading` untouched here is deliberate: if a
    // newer request for the CURRENT duration is still in flight, clearing it now would
    // make the calendar look ready one render early, reopening the exact race this
    // tagging exists to close.
    if (responseTripPriceId !== state.experienceBookingData.requestedTripPriceId) return

    state.experienceBookingData.loading = false
    state.experienceBookingData.bookingDataTripPriceId = responseTripPriceId
    state.experienceBookingData.bookingData = {
      ...state.experienceBookingData.bookingData,
      ...action.payload
    }
  },
  [failedExperienceBookingData]: (state, action) => {
    const responseTripPriceId = action.props?.tripPriceId ?? null
    if (responseTripPriceId !== state.experienceBookingData.requestedTripPriceId) return

    state.experienceBookingData.loading = false
    state.experienceBookingData.error = action.payload
  },
  [requestedEmbeddedCreateBooking]: (state) => {
    state.bookingInfo.loading = true
    state.bookingInfo.error = null
  },
  [receivedEmbeddedCreateBooking]: (state, action) => {
    state.bookingInfo.loading = false
    state.bookingInfo.guestToken = action.payload.guest_token
    state.bookingInfo.data = {
      ...state.bookingInfo.data,
      ...action.payload
    }
  },
  [failedEmbeddedCreateBooking]: (state, action) => {
    state.bookingInfo.loading = false
    state.bookingInfo.error = action.payload
  },
  [requestedEmbeddedUpdateBooking]: (state) => {
    state.bookingInfo.loading = true
    state.bookingInfo.error = null
  },
  [receivedEmbeddedUpdateBooking]: (state, action) => {
    state.bookingInfo.loading = false
    state.bookingInfo.data = {
      ...state.bookingInfo.data,
      ...action.payload
    }
  },
  [failedEmbeddedUpdateBooking]: (state, action) => {
    state.bookingInfo.loading = false
    state.bookingInfo.error = action.payload
  },
  [requestedEmbeddedGetBookingDetailsById]: (state) => {
    state.confirmedBookingData.loading = true
    state.confirmedBookingData.error = null
  },
  [receivedEmbeddedGetBookingDetailsById]: (state, action) => {
    state.confirmedBookingData.loading = false
    state.confirmedBookingData.data = {
      ...action.payload
    }
  },
  [failedEmbeddedGetBookingDetailsById]: (state, action) => {
    state.confirmedBookingData.loading = false
    state.confirmedBookingData.error = action.payload
  },
  [updateBookingSelections]: (state, action) => {
    state.experienceBookingData.bookingSelections = {
      ...state.experienceBookingData.bookingSelections,
      ...action.payload
    }
  },
  [resetBookingSelections]: (state) => {
    state.experienceBookingData.bookingSelections = initialState.experienceBookingData.bookingSelections
  },
  // The same experience on the same date and start time replaces the item already in the cart (its
  // add-ons kept); another date or time is a new item. The item's hold comes with receivedWidgetCart.
  [addWidgetCartItem]: (state, action) => {
    const item = action.payload
    const index = state.widgetCart.items.findIndex((existing) => isSameCartSlot(existing, item))
    if (index === -1) {
      state.widgetCart.items.push(item)
      return
    }
    const existing = state.widgetCart.items[index]
    state.widgetCart.items[index] = { ...item, addOns: existing.addOns || item.addOns }
  },
  [receivedWidgetCart]: (state, action) => {
    const cart = action.payload || {}
    const holdsByItem = Object.fromEntries((cart.holds || []).map((hold) => [hold.client_item_id, hold]))
    state.widgetCart.cartToken = cart.cart_token || state.widgetCart.cartToken
    state.widgetCart.hold = mapCartHold(cart, action.meta.receivedAt)
    state.widgetCart.items = state.widgetCart.items.map((item) => ({ ...item, ...mapItemHold(holdsByItem[item.cartId]) }))
  },
  [forgetWidgetCartHolds]: (state) => {
    state.widgetCart.cartToken = null
    state.widgetCart.hold = null
    state.widgetCart.items = state.widgetCart.items.map((item) => ({ ...item, ...NO_ITEM_HOLD }))
  },
  [loseWidgetCartItemHold]: (state, action) => {
    const { cartId, error } = action.payload
    const index = state.widgetCart.items.findIndex((item) => item.cartId === cartId)
    if (index !== -1) state.widgetCart.items[index] = { ...state.widgetCart.items[index], holdStatus: CART_HOLD_STATUSES.lost, holdError: error }
  },
  [updateWidgetCartItem]: (state, action) => {
    const { cartId, changes } = action.payload
    const index = state.widgetCart.items.findIndex((item) => item.cartId === cartId)
    if (index !== -1) state.widgetCart.items[index] = { ...state.widgetCart.items[index], ...changes }
  },
  [removeWidgetCartItem]: (state, action) => {
    state.widgetCart.items = state.widgetCart.items.filter((item) => item.cartId !== action.payload)
    if (!state.widgetCart.items.length) state.widgetCart.hold = null
  },
  [restoreWidgetCart]: (state, action) => {
    state.widgetCart.items = action.payload?.items || []
    state.widgetCart.cartToken = action.payload?.cartToken || null
    state.widgetCart.hold = state.widgetCart.items.length && state.widgetCart.cartToken ? action.payload?.hold || null : null
  },
  [clearWidgetCart]: (state) => {
    state.widgetCart = initialState.widgetCart
  },
  [setWidgetCartPaying]: (state, action) => {
    state.widgetCart.isPaying = !!action.payload
  },
  [resetExperienceBookingData]: (state) => {
    state.experienceBookingData = initialState.experienceBookingData
  },
  [resetExperienceById]: (state) => {
    state.experienceById = initialState.experienceById
  },
  [resetBookingInfo]: (state) => {
    state.bookingInfo = initialState.bookingInfo
  },
  [cleanExperiencesList]: (state) => {
    state.list = []
  },
  [requestedTripAvailableSlots]: (state) => {
    state.tripAvailableSlots.loading = true
    state.tripAvailableSlots.error = null
  },
  [receivedTripAvailableSlots]: (state, action) => {
    state.tripAvailableSlots.loading = false
    state.tripAvailableSlots.data = action.payload
  },
  [failedTripAvailableSlots]: (state, action) => {
    state.tripAvailableSlots.loading = false
    state.tripAvailableSlots.error = action.payload
  },
  [resetTripAvailableSlots]: (state) => {
    state.tripAvailableSlots = initialState.tripAvailableSlots
  },
  [requestedEmail]: (state) => setRequested(state),
  [receivedEmail]: (state, action) => {
    setReceived(state)
    state.list = action.payload
  },
  [failedEmail]: (state) => setFailed(state)
})

export default experiencesReducer

export const createEmail = (data) => (dispatch) => {
  return dispatch(
    api({
      url: "/user/send_invitation_email",
      data,
      method: "post",
      onStart: requestedEmail.type,
      onSuccess: receivedEmail.type,
      onError: failedEmail.type
    })
  )
}

export const fetchExperiences = (list_for, duration_type, fields) =>
  api({
    url:
      "/guide/trips/embedded_experiences_list" +
      (list_for ? `?list_for=${list_for}` : "") +
      (duration_type ? `&duration_type=${duration_type}` : "") +
      (fields ? `&fields=${fields}` : ""),
    onStart: requestedExperiences.type,
    onSuccess: receivedExperiences.type,
    onError: failedExperiences.type
  })

// Booking widget calls send the cart's token once the guest has one: availability then counts the
// guest's own held places as free (so they can edit their own item), and cart calls find the cart.
// Other screens never have a cart token, so their calls are unchanged.
const getCartTokenHeaders = (getState) => {
  const cartToken = getState().experiences.widgetCart.cartToken
  return cartToken ? { "Cart-Token": cartToken } : undefined
}
const withCartToken = (config) => (dispatch, getState) => dispatch(api({ ...config, headers: getCartTokenHeaders(getState) }))

// `guests` keeps only the experiences one booking can take a party of that size on (API filter).
const getGuestsParam = (guests) => (guests ? `&guests=${guests}` : "")

export const fetchExperiencesByGuide = (uuid, date, page = 1, guests = null) =>
  withCartToken({
    url: `/embedded_experiences?guuid=${uuid}&page=${page}&date=${date}${getGuestsParam(guests)}`,
    onStart: requestedExperiencesByGuide.type,
    onSuccess: receivedExperiencesByGuide.type,
    onError: failedExperiencesByGuide.type
  })

// The first page of the guide's list for a date and party size, without touching the store: the
// widget's phone Filters sheet counts the results before the guest applies them.
export const fetchExperiencesPreviewByGuide = (uuid, date, guests = null) =>
  withCartToken({
    url: `/embedded_experiences?guuid=${uuid}&page=1&date=${date}${getGuestsParam(guests)}`,
    suppressErrorNotification: true
  })

// A ticketed experience's start times on `date` (the guide's local date), each with places left in
// total and per ticket type, without touching the store: the booking panel keeps them for the
// chosen date. { data: [{ id, starts_at, bookable, sold_out, remaining, ticket_types: [...] }] }
export const fetchTicketedStartTimes = (id, date) =>
  withCartToken({
    url: `/embedded_experiences/${id}/booking_details?type=available_slots&embedded=true&date=${date}`,
    suppressErrorNotification: true
  })

// The days from `from` to `to` (YYYY-MM-DD, at most 62 days) on which a ticketed experience has a
// start time left: { data: { bookable_dates: ["YYYY-MM-DD"] } }.
export const fetchTicketedBookableDates = (id, from, to) =>
  withCartToken({
    url: `/embedded_experiences/${id}/booking_details?type=available_slots&embedded=true&from=${from}&to=${to}`,
    suppressErrorNotification: true
  })

export const fetchExperienceById = (id, b_uuid, embedded, clientId) => {
  let url = `/embedded_experiences/${id}?include_unavailable_dates=true`

  if (b_uuid) {
    url += `&b_uuid=${b_uuid}`
  }

  if (!embedded) {
    url += "&embedded=true"
  }

  // Guide portal only: the client being booked, so their own unfinished checkout does not grey
  // out the date in sold_out_dates (the backend ignores it on non-guide requests).
  if (clientId) {
    url += `&client_id=${clientId}`
  }

  return withCartToken({
    url,
    onStart: requestedExperienceById.type,
    onSuccess: receivedExperienceById.type,
    onError: failedExperienceById.type
  })
}

export const fetchExperienceBookingDetails = (id, type, trip_duration, date, bookingId, mode, clientId) => {
  const params = new URLSearchParams({
    type,
    trip_price_id: trip_duration ?? "",
    booking_date: date ?? "",
    b_uuid: bookingId
  })
  if (mode === BOOKING_MODES.embedded) params.append("embedded", "true")
  // Guide Profile bookings share the "guide's own site" availability rule with the embedded
  // widget (Front-end Visibility), not the Marketplace toggle — see check_trip_for_booking.
  if (mode === BOOKING_MODES.guideProfile) params.append("guide_profile", "true")
  if (clientId) params.append("client_id", clientId)

  return withCartToken({
    url: `embedded_experiences/${id}/booking_details?${params}`,
    onStart: requestedExperienceBookingData.type,
    onSuccess: receivedExperienceBookingData.type,
    onError: failedExperienceBookingData.type,
    // Tags the request/response with the duration it's FOR (normalizing "" to null the
    // same way the single-day trip_data call's empty trip_duration does), so the reducer
    // can match a response against what's currently requested — see the reducer handlers.
    props: { tripPriceId: trip_duration || null }
  })
}

// `isQuiet`: no error toast, for a caller that handles the refusal itself (a create from the cart
// that retries without its hold).
export const createEmbeddededBooking =
  (data, { isQuiet = false } = {}) =>
  (dispatch) =>
    dispatch(
      withCartToken({
        url: `/embedded_experiences`,
        data,
        method: "post",
        suppressErrorNotification: isQuiet,
        onStart: requestedEmbeddedCreateBooking.type,
        onSuccess: receivedEmbeddedCreateBooking.type,
        onError: failedEmbeddedCreateBooking.type
      })
    )

export const updateEmbeddededBooking = (bookingId, data) => (dispatch) => {
  return dispatch(
    api({
      url: `/embedded_experiences/${bookingId}`,
      data,
      method: "put",
      onStart: requestedEmbeddedUpdateBooking.type,
      onSuccess: receivedEmbeddedUpdateBooking.type,
      onError: failedEmbeddedUpdateBooking.type
    })
  )
}

// Booking widget cart holds: the API holds the cart's places from Add to Cart (/cart/holds, one
// timer for the whole cart). Every cart call answers with the whole cart, put in the store with
// receivedWidgetCart. Extension and renewal follow a guest's action only, never a timer.

export const hasCartError = (error, key) => !!error?.data?.message?.[key]

const HOLDING_STATUSES = new Set([CART_HOLD_STATUSES.held, CART_HOLD_STATUSES.expired])

// A cart call: the API's cart (`data`), errors left to the caller (each screen shows its own).
const cartRequest = (config) => async (dispatch) => (await dispatch(withCartToken({ suppressErrorNotification: true, ...config })))?.data

const holdItem = (item) => cartRequest({ url: "/cart/holds", method: "post", data: buildCartHoldPayload(item) })
const changeItemHold = (item) => cartRequest({ url: `/cart/holds/${item.holdId}`, method: "patch", data: buildCartHoldPayload(item) })
const releaseItemHold = (holdId) => (dispatch) => dispatch(cartRequest({ url: `/cart/holds/${holdId}`, method: "delete" })).catch(() => null)

// Page reload or widget reopened: the cart as the API has it. A cart it no longer knows holds nothing.
export const fetchWidgetCart = () => async (dispatch, getState) => {
  if (!getState().experiences.widgetCart.cartToken) return
  try {
    dispatch(receivedWidgetCart(await dispatch(cartRequest({ url: "/cart" }))))
  } catch (error) {
    if (hasCartError(error, CART_ERRORS.holdNotFound)) dispatch(forgetWidgetCartHolds())
  }
}

// "Check availability & continue" after the timer ran out: each item the API held comes back held, or
// lost with the reason. Nothing to do while the timer runs, or when the API holds nothing for this cart.
const renewExpiredWidgetCart = () => async (dispatch, getState) => {
  const { cartToken, hold, items } = getState().experiences.widgetCart
  const isRunning = hold && hold.expiresAt > Date.now()
  if (!cartToken || isRunning || !items.some((item) => HOLDING_STATUSES.has(item.holdStatus))) return
  try {
    dispatch(receivedWidgetCart(await dispatch(cartRequest({ url: "/cart/renewal", method: "post" }))))
  } catch (error) {
    if (hasCartError(error, CART_ERRORS.holdNotFound)) dispatch(forgetWidgetCartHolds())
    // This device's clock ran ahead: the API's timer is still running.
    else if (hasCartError(error, CART_ERRORS.holdNotExpired)) await dispatch(fetchWidgetCart())
    else throw error
  }
}

// Add to Cart and Book Now: the item's places are held, then it goes in the cart. The same date and
// start time already held in the cart is changed instead (its hold updated, not held twice); one
// that's no longer held is replaced. Refused like a booking create when the places aren't there
// (the API's error is thrown), and the cart is then left as it was. A cart whose reservation ran out
// is never renewed here: the API refuses with hold_expired (thrown, the cart read again) until the
// guest presses "Check availability & continue".
export const addToWidgetCart = (selection) => async (dispatch, getState) => {
  const existing = getState().experiences.widgetCart.items.find((item) => isSameCartSlot(item, selection))
  const isChange = existing?.holdStatus === CART_HOLD_STATUSES.held
  const item = { ...selection, cartId: isChange ? existing.cartId : nanoid(), holdId: isChange ? existing.holdId : null }
  if (existing?.holdId && !isChange) await dispatch(releaseItemHold(existing.holdId))
  let cart
  try {
    cart = await dispatch(isChange ? changeItemHold(item) : holdItem(item))
  } catch (error) {
    if (hasCartError(error, CART_ERRORS.holdExpired)) {
      await dispatch(fetchWidgetCart())
      throw error
    }
    if (!hasCartError(error, CART_ERRORS.holdNotFound) || !getState().experiences.widgetCart.cartToken) throw error
    // The API no longer knows this cart: the item starts a new one.
    dispatch(forgetWidgetCartHolds())
    cart = await dispatch(holdItem(item))
  }
  dispatch(addWidgetCartItem(item))
  dispatch(receivedWidgetCart(cart))
}

// "Choose another time" on a cart item (one whose places are gone): its hold changes to the new choice
// (PATCH, the cart's timer unchanged), so the item keeps its id and add-ons; a lost item that fits
// again is held again. Refused like Add to Cart when the places aren't there (thrown), and the item is
// then left as it was; hold_expired reads the cart again first, as Add to Cart does.
export const changeWidgetCartItemChoice = (cartId, selection) => async (dispatch, getState) => {
  const existing = getState().experiences.widgetCart.items.find((item) => item.cartId === cartId)
  const item = { ...existing, ...selection, cartId, holdId: existing.holdId, addOns: existing.addOns }
  let cart
  try {
    cart = await dispatch(changeItemHold(item))
  } catch (error) {
    if (hasCartError(error, CART_ERRORS.holdExpired)) await dispatch(fetchWidgetCart())
    throw error
  }
  dispatch(updateWidgetCartItem(cartId, item))
  dispatch(receivedWidgetCart(cart))
}

// Remove: out of the cart at once, and its places released.
export const removeFromWidgetCart = (cartId) => async (dispatch, getState) => {
  const item = getState().experiences.widgetCart.items.find((cartItem) => cartItem.cartId === cartId)
  dispatch(removeWidgetCartItem(cartId))
  if (!item?.holdId || item.holdStatus === CART_HOLD_STATUSES.booked) return
  const cart = await dispatch(releaseItemHold(item.holdId))
  if (cart) dispatch(receivedWidgetCart(cart))
}

// Checkout lowered an item to the places left: its hold follows. If the API refuses, Pay books
// without the hold (the places are checked then anyway).
export const changeWidgetCartItem = (cartId, changes) => async (dispatch, getState) => {
  dispatch(updateWidgetCartItem(cartId, changes))
  const item = getState().experiences.widgetCart.items.find((cartItem) => cartItem.cartId === cartId)
  if (item?.holdStatus !== CART_HOLD_STATUSES.held) return
  try {
    dispatch(receivedWidgetCart(await dispatch(changeItemHold(item))))
  } catch (error) {
    // Left to Pay, as above.
  }
}

// "Still shopping?" -> "Yes, give me 5 more minutes" (once per reservation). Refused once no extension
// is left or the time ran out: the cart is then shown as the API has it.
export const extendWidgetCart = () => async (dispatch) => {
  try {
    dispatch(receivedWidgetCart(await dispatch(cartRequest({ url: "/cart/extension", method: "post" }))))
  } catch (error) {
    await dispatch(fetchWidgetCart())
  }
}

// "Release my reservation": the places are freed at once; the items stay in the cart, no longer
// reserved. Refused once the time ran out: the cart is then shown as the API has it.
export const releaseWidgetCartHolds = () => async (dispatch) => {
  try {
    dispatch(receivedWidgetCart(await dispatch(cartRequest({ url: "/cart/release", method: "post" }))))
  } catch (error) {
    await dispatch(fetchWidgetCart())
  }
}

// "Check availability & continue": an expired cart is renewed, and items the API doesn't hold (its
// cart was gone, or the item was saved before holds) are held again, one by one so they share one
// cart. An item whose places are gone is marked lost with the API's reason; any other failure is
// thrown. Resolves true when no item ended up lost.
export const resumeWidgetCartHolds = () => async (dispatch, getState) => {
  await dispatch(renewExpiredWidgetCart())
  const unheld = getState().experiences.widgetCart.items.filter((item) => !item.holdStatus)
  for (const item of unheld) {
    try {
      dispatch(receivedWidgetCart(await dispatch(holdItem(item))))
    } catch (error) {
      if (error?.status !== 400) throw error
      dispatch(loseWidgetCartItemHold(item.cartId, error.data?.message || null))
    }
  }
  return !getState().experiences.widgetCart.items.some((item) => item.holdStatus === CART_HOLD_STATUSES.lost)
}

// The cart is given up (its experience can't be booked): its places are released and it is emptied.
export const releaseWidgetCart = () => async (dispatch, getState) => {
  if (getState().experiences.widgetCart.cartToken) await dispatch(cartRequest({ url: "/cart", method: "delete" })).catch(() => null)
  dispatch(clearWidgetCart())
}

// Booking widget orders: two or more cart items booked and paid together, in one payment. The
// create takes the cart's holds (Cart-Token); every later call carries the order's guest token.
// Errors are left to the checkout, which names the cart item an error is about. Each resolves with
// the order ({ uuid, order_number, status, amounts, bookings, ... }) or the payment intent.
const orderRequest = (config) => async (dispatch) => {
  const body = await dispatch(api({ suppressErrorNotification: true, refresh: false, ...config }))
  return body?.data ?? body
}

export const createWidgetOrder = (order) => async (dispatch) =>
  (await dispatch(withCartToken({ url: "/orders", method: "post", data: { order }, suppressErrorNotification: true })))?.data

export const updateWidgetOrder = (uuid, guestToken, order) => orderRequest({ url: `/orders/${uuid}`, method: "put", guestToken, data: { order } })

export const fetchWidgetOrder = (uuid, guestToken) => orderRequest({ url: `/orders/${uuid}`, guestToken })

// { id, client_secret, status }; no client_secret when nothing is left to pay now, and
// { card_payments_available: false } when the guide takes no cards.
export const createWidgetOrderPayment = (uuid, guestToken) => orderRequest({ url: `/orders/${uuid}/payments`, method: "post", guestToken })

export const captureWidgetOrderPayment = (uuid, guestToken, paymentIntentId) =>
  orderRequest({ url: `/orders/${uuid}/payments/${paymentIntentId}/capture`, method: "put", guestToken })

export const createPaymentIntent = (bookingId, guestToken, promoCodes) => (dispatch) => {
  return dispatch(
    api({
      url: `trip_bookings/${bookingId}/payments`,
      method: "post",
      guestToken,
      data: promoCodes?.length ? { promo_codes: promoCodes } : undefined,
      onStart: requestedEmbeddedCreatePaymentIntent.type,
      onSuccess: receivedEmbeddedCreatePaymentIntent.type,
      onError: failedEmbeddedCreatePaymentIntent.type
    })
  )
}

export const capturePayment = (bookingId, recordId) => (dispatch) => {
  return dispatch(
    api({
      url: `/trip_bookings/${bookingId}/payments/${recordId}/capture`,
      method: "put"
    })
  )
}

export const fetchTripAvailableSlots = (tripId, date) =>
  api({
    url: `/trips/${tripId}/available_slots${date ? `?date=${date}` : ""}`,
    onStart: requestedTripAvailableSlots.type,
    onSuccess: receivedTripAvailableSlots.type,
    onError: failedTripAvailableSlots.type
  })

export const fetchEmbeddedGuestToken = () => () => {
  // guest_token is stored in localStorage by api.js automatically when a booking
  // is created via POST /embedded_experiences (createEmbeddededBooking).
  return localStorage.getItem("guest_token")
}

export const getBookingDetailsById = (bookingId) => (dispatch) => {
  return dispatch(
    api({
      url: `/trip_bookings/${bookingId}?include_stripe_cards=true`,
      onStart: requestedEmbeddedGetBookingDetailsById.type,
      onSuccess: receivedEmbeddedGetBookingDetailsById.type,
      onError: failedEmbeddedGetBookingDetailsById.type
    })
  )
}
